import {useEffect, useRef, useState} from 'react'

const ANIMATION_STYLE_ID = 'slide-up-animation-keyframes'

const injectStyles = () => {
    if (document.getElementById(ANIMATION_STYLE_ID)) return
    const style = document.createElement('style')
    style.id = ANIMATION_STYLE_ID
    style.textContent = `
        @keyframes kfSlideUp {
            from { opacity: 0; transform: translateY(80px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .kf-card-animate {
            opacity: 0;
            animation: kfSlideUp 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
    `
    document.head.appendChild(style)
}

/**
 * Observe a single element independently.
 * Returns { ref, getAnimProps } where getAnimProps(index) fires only when
 * that specific element enters the viewport.
 */
const useAnimRef = ({threshold = 0.4, stagger = 0.2} = {}) => {
    const ref = useRef(null)
    const [isVisible, setIsVisible] = useState(false)
    const [isHydrated, setIsHydrated] = useState(false)

    useEffect(() => {
        injectStyles()
        setIsHydrated(true)
    }, [])

    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect()
                }
            },
            {threshold},
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [threshold])

    const getAnimProps = (index = 0) =>
        !isHydrated
            ? {}
            : isVisible
            ? {className: 'kf-card-animate', style: {animationDelay: `${index * stagger}s`}}
            : {style: {opacity: 0}}

    return {ref, getAnimProps}
}

/**
 * Injects the kfSlideUp keyframe styles once and returns:
 *   - containerRef: attach to the wrapper element to observe
 *   - getAnimProps(index): returns className + animationDelay props for each animated child
 *
 * @param {object} options
 * @param {number} [options.threshold=0.25]  - IntersectionObserver threshold
 * @param {number} [options.stagger=0.2]    - delay in seconds between each item
 */
const useSlideUpAnimation = ({threshold = 0.4, stagger = 0.2} = {}) => {
    const containerRef = useRef(null)
    const [isVisible, setIsVisible] = useState(false)
    const [isHydrated, setIsHydrated] = useState(false)

    useEffect(() => {
        injectStyles()
        setIsHydrated(true)
    }, [])

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true)
                    observer.disconnect()
                }
            },
            {threshold},
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [threshold])

    const getAnimProps = (index) =>
        !isHydrated
            ? {}
            : isVisible
            ? {className: 'kf-card-animate', style: {animationDelay: `${index * stagger}s`}}
            : {style: {opacity: 0}}

    return {containerRef, getAnimProps}
}

export {useAnimRef}
export default useSlideUpAnimation
